/**
 * navigateHash — SSOT for programmatic hash navigation in the
 * mentorship rail (and any other surface that uses hash-based
 * deep links).
 *
 * Why a helper instead of inline `window.location.hash = ...`?
 *
 * `window.location.hash = X` works but pushes a history entry. The
 * rail-layout primitive itself (`RailLayout.selectSection`) uses
 * `history.replaceState` so a section click doesn't pollute the
 * back stack. To keep semantics consistent across click sources
 * (rail nav, KPI tile, Command Palette), every programmatic hash
 * jump should route through here.
 *
 * Mechanics: `replaceState` doesn't fire `hashchange`, so we
 * dispatch one manually — the rail's listener (Phase 2a) is what
 * actually flips the active section.
 */

export function navigateHash(hash: string): void {
    if (typeof window === 'undefined') return;
    // Normalize to always start with '#' so callers can pass either
    // 'reviews' or '#reviews' without ambiguity.
    const normalized = hash.startsWith('#') ? hash : `#${hash}`;
    // Same-hash navigation: no-op so we don't fire a redundant event.
    if (window.location.hash === normalized) return;

    window.history.replaceState(null, '', normalized);
    // replaceState doesn't fire hashchange; dispatch one so RailLayout's
    // listener flips the active section.
    window.dispatchEvent(new HashChangeEvent('hashchange'));
}
